import { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import DateRangePicker from "../../shared/components/DateRangePicker";
import ComboBox from "../../shared/components/form/ComboBox";
import debounce from 'lodash/debounce';
import moment from 'moment';
import cx from 'classnames';
import { MagnifyingGlassIcon, CalendarDaysIcon, XMarkIcon, AdjustmentsHorizontalIcon } from "@heroicons/react/24/outline";
import { fetchRoutes } from "../../../data/shared/api/api";
import { Desktop, Mobile } from "../../shared/components/Responsive";
import { faCircleNotch } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

export default function Filter({filters, type, status}) {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const containerRef = useRef(null);
    const filterRef = useRef(null);

    const { isFetching } = useSelector(state => state.account.booking);
    const [routes, setRoute] = useState([]);
    const [calendarVisible, setCalendarVisible] = useState(false);
    const [filterVisible, setFilterVisible] = useState(false);
    const [routeValue, setRouteValue] = useState(filters.route || null);
    const [statusValue, setStatusValue] = useState(filters.status != [] ? null : filters.status);
    const [queryValue, setQueryValue] = useState(filters.query || "");

    const filteredTrips = (filters) => {
        dispatch(type(filters));
    }

    const handleHikeSelection = (selectedRoute) => {
        setRouteValue(selectedRoute ? selectedRoute : null);
        dispatch(type({ route: selectedRoute ? selectedRoute.id : null}));
    };

    const handleStatusSelection = (selectedStatus) => {
        setStatusValue(selectedStatus ? selectedStatus : null);
        dispatch(type({ status: selectedStatus ? selectedStatus.id : null}));
    };

    useEffect(()=> {
        fetchRoutes().then(response =>response.json())
        .then(json => {
            setRoute(json)
        })
        
        const handleOutsideClick = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setCalendarVisible(false);
            }
            if (filterRef.current && !filterRef.current.contains(event.target)) {
                setFilterVisible(false);
            }
        };
      
        window.addEventListener('mousedown', handleOutsideClick);
      
        return () => {
            window.removeEventListener('mousedown', handleOutsideClick);
        };
    }, [])

    const throttledSearch = debounce((e) => {
        filteredTrips({'query':e.target.value});
    }, 500);

    const handleQuerySelection = (e) => {
        setQueryValue(e.target.value);
        throttledSearch(e);
    }
    return (<>
        <Desktop>
            <div className="flex flex-row justify-center items-center my-3 shadow-md rounded-l-md rounded-tr-md bg-gray-100">
                <div className="flex justify-center py-2 border-none">
                    {isFetching
                        ? <FontAwesomeIcon icon={faCircleNotch} className={"h-6 w-6 mx-2 text-gray-400"} spin />
                        : <MagnifyingGlassIcon className="h-6 w-6 mx-2 text-gray-600" />
                    }
                </div>
                <input 
                    value={queryValue}  placeholder={t('account.file_number')}
                    onChange={(e) => handleQuerySelection(e)} 
                    className="flex-1 min-w-[250px] py-2 px-3 my-1 bg-gray-100 border-none outline-none focus:ring-2 focus:ring-[#F97316] rounded-md" maxLength="28"
                />
                <span className="block w-px bg-gray-300 h-[30px] mt-2 mb-1 mx-1"/>
                <div className="flex-1">
                    <ComboBox
                        value={routeValue}
                        options={routes}
                        onChange={handleHikeSelection}
                        placeholder={t('account.path')}
                        className={"w-full focus:ring-2 focus:ring-[#F97316] rounded-md mb-1 pr-[3.5rem] bg-gray-100 border-none focus:border-none"}
                        isLoading={false}
                    />
                </div>
                <span className="block w-px bg-gray-300 h-[30px] mt-2 mb-1 mx-1"/>
                <div className="flex-1">
                    <ComboBox
                        value={statusValue}
                        options={status}
                        onChange={handleStatusSelection}
                        placeholder={t('account.status')}
                        className={"w-full focus:ring-2 focus:ring-[#F97316] rounded-md mb-1 pr-[3.5rem] bg-gray-100 border-none focus:border-none"}
                        isLoading={false}
                    />
                </div>
                <span className="block w-px bg-gray-300 h-[30px] mt-2 mb-1 mx-1"/>
                <div className="relative flex items-center py-3">
                    {!calendarVisible 
                    ? <button onClick={() => setCalendarVisible(true)} className="h-full"><CalendarDaysIcon className="h-8 w-8 mx-4 text-gray-600" /></button>
                    : <button onClick={() => setCalendarVisible(false)} className="h-full"><XMarkIcon className="h-8 w-8 mx-4 text-gray-600" /></button>
                    }
                    {calendarVisible && (
                        <div ref={containerRef} className="absolute w-auto top-full left-auto right-0 z-20 bg-gray-100 shadow-[0px_0px_25px_0px_rgba(0,0,0,0.2)] p-2 mt-2 rounded">
                            <DateRangePicker selectedValue={filters.date} onChange={(range)=>{
                                dispatch(type(range));
                                setCalendarVisible(false)
                            }}/>
                        </div>
                    )}
                </div>
            </div>
        </Desktop>

        <Mobile>
            <div className="flex flex-col items-center my-4">
                <div className="flex gap-2 relative">
                    <div className="flex justify-center items-center p-2 rounded-md">
                        {isFetching
                            ? <FontAwesomeIcon icon={faCircleNotch} className={"h-6 w-6 text-gray-400"} spin />
                            : <MagnifyingGlassIcon className="h-6 w-6 text-gray-600" />
                        }
                    </div>
                    <div ref={filterRef}>
                        <div onClick={() => setFilterVisible(!filterVisible)}
                            className={cx("w-fit h-full flex items-center gap-2 p-2 border rounded-md hover:shadow-inner text-lg cursor-pointer", {
                                "bg-gray-500 hover:bg-gray-600 text-white": filterVisible,
                                "bg-gray-100 hover:bg-gray-200 text-gray-800": !filterVisible
                            })}
                        >
                            <p>{t("account.filters")}</p>
                            {!filterVisible 
                            ? <AdjustmentsHorizontalIcon className="h-6 w-6" aria-hidden="true" />
                            : <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                            }
                        </div>
                        {filterVisible && (
                            <div className="w-[400px] max-w-[90vw] absolute top-full left-1/2 transform -translate-x-1/2 z-20 flex flex-col justify-center bg-stone-200 border border-gray-300 shadow-[0px_0px_25px_0px_rgba(0,0,0,0.2)] p-4 mt-2 rounded">
                                <input 
                                    value={queryValue} placeholder={t('account.file_number')} 
                                    onChange={(e) => handleQuerySelection(e)} 
                                    className="flex-1 py-2 px-3 my-1 bg-gray-100 border border-gray-300 outline-none focus:border-gray-300 focus:ring-2 focus:ring-[#F97316] rounded-md" maxLength="28"
                                />
                                <div className="flex-1">
                                    <ComboBox
                                        value={routeValue}
                                        options={routes}
                                        onChange={handleHikeSelection}
                                        placeholder={t('account.path')}
                                        className={"w-full mb-1 pr-[3.5rem] bg-gray-100 border border-gray-300 focus:border-gray-300 focus:ring-2 focus:ring-[#F97316] rounded-md"}
                                        isLoading={false}
                                    />
                                </div>
                                <div className="flex-1">
                                    <ComboBox
                                        value={statusValue}
                                        options={status}
                                        onChange={handleStatusSelection}
                                        placeholder={t('account.status')}
                                        className={"w-full mb-1 pr-[3.5rem] bg-gray-100 border border-gray-300 focus:border-gray-300 focus:ring-2 focus:ring-[#F97316] rounded-md"}
                                        isLoading={false}
                                    />
                                </div>
                            </div>
                        )}

                    </div>
                    <div ref={containerRef}>
                        <div onClick={() => setCalendarVisible(!calendarVisible)}
                            className={cx("group flex items-center gap-2 text-center text-sm px-2 py-1 border rounded-md hover:shadow-inner cursor-pointer max-w-[170px]", {
                                "bg-gray-500 hover:bg-gray-600": calendarVisible,
                                "bg-gray-100 hover:bg-gray-200": !calendarVisible
                        })}>
                            <div className={cx("", {
                                "text-white": calendarVisible,
                                "text-gray-800": !calendarVisible
                            })}>
                                {!calendarVisible 
                                ? <CalendarDaysIcon className="h-8 w-8" />
                                : <XMarkIcon className="h-6 w-6 mx-1" />
                                }
                            </div>

                            <div>
                                {filters.date.from ? <div className={cx("flex flex-col justify-center items-center", {
                                    "text-white": calendarVisible,
                                    "text-gray-800": !calendarVisible
                                })}>
                                    <p>{moment(filters.date.from).format("DD MMM YYYY")}</p>
                                    <span className={cx("w-1/3 h-px", {
                                        "bg-white": calendarVisible,
                                        "bg-gray-800": !calendarVisible
                                    })}></span>
                                    <p>{filters.date.to && moment(filters.date.to).format("DD MMM YYYY")}</p>
                                </div> : <p className={cx("text-gray-400", {
                                    "text-white": calendarVisible,
                                    "text-gray-800": !calendarVisible
                                })}>
                                    {t('account.no_selected_date')}
                                </p>}
                            </div>
                        </div>
                        {calendarVisible && (
                            <div className="absolute top-full left-1/2 transform -translate-x-1/2 z-20 bg-gray-100 shadow-[0px_0px_25px_0px_rgba(0,0,0,0.2)] p-2 mt-2 rounded">
                                <DateRangePicker selectedValue={filters.date} onChange={(range)=>{
                                    dispatch(type(range));
                                    setCalendarVisible(false)
                                }}/>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </Mobile>
    </>)
}